import React, { useState, useEffect } from 'react';
import { Shield, Zap, Crown, Check, Lock, User as UserIcon } from 'lucide-react';

export default function CheckoutPage({ 
  user, 
  onRequireLogin, 
  onInitiatePayment, 
  onNavigate, 
  initialPlan = 'single_49',
  hasActiveAnalysis = false
}) {
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);

  useEffect(() => {
    if (initialPlan) {
      setSelectedPlan(initialPlan);
    }
  }, [initialPlan]);

  const planDetails = {
    single_49: {
      id: 'single_49',
      name: 'Pay Per Resume (Single Optimization)',
      tagline: '1 Fact-Locked ATS Tailored Resume',
      originalPrice: 149,
      price: 49,
      discount: '67% OFF',
      features: [
        '1 ATS-Optimized Tailored Resume PDF',
        'Direct alignment to target Job Description',
        'Fact-Locked Zero-Fabrication guarantee',
        'Single-Page Dynamic Auto-Fit',
        'Instant download & print-ready export'
      ]
    },
    premium_799: {
      id: 'premium_799',
      name: 'Premium Monthly (20 Resumes Package)',
      tagline: '20 Tailored Resumes for Active Job Seekers',
      originalPrice: 2299,
      price: 799,
      discount: '65% OFF',
      features: [
        '20 Full-Fidelity Tailored Resume PDFs',
        'Just ₹39.95 per resume (Save ₹180)',
        'All 3 Templates Unlocked with instant re-generation',
        'Unlimited ATS Keyword Gap & Diagnostic Scans',
        'Cloud Backup in My Resumes dashboard (30-day validity)',
        'Priority render processing'
      ]
    }
  };

  const current = planDetails[selectedPlan] || planDetails.single_49;

  const handlePay = () => {
    if (!user) {
      onRequireLogin();
      return;
    }
    onInitiatePayment(selectedPlan);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Breadcrumb & Navigation */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <button 
            onClick={() => onNavigate('/pricing')}
            className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold flex items-center gap-1 transition"
          >
            ← Back to All Plans
          </button>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <span>256-Bit SSL Secure Checkout</span>
          </div>
        </div>

        {/* Page Title */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-white">Review & Complete Your Order</h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">Select your plan and complete payment securely via Razorpay (UPI, Cards, NetBanking).</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Plan Selection & Order Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Selector Tabs */}
            <div className="space-y-3">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">Choose Your Plan</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Single Plan Button */}
                <div 
                  onClick={() => setSelectedPlan('single_49')}
                  className={`p-4 rounded-2xl border cursor-pointer transition flex flex-col justify-between ${
                    selectedPlan === 'single_49'
                      ? 'bg-indigo-950/40 border-indigo-500 shadow-md shadow-indigo-500/10'
                      : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-white flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-indigo-400" />
                      Single Resume
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-indigo-300">
                      Save 67%
                    </span>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-black text-white">₹49</span>
                      <span className="text-xs text-slate-500 line-through">₹149</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">1 tailored resume</p>
                  </div>
                </div>

                {/* Monthly Plan Button */}
                <div 
                  onClick={() => setSelectedPlan('premium_799')}
                  className={`p-4 rounded-2xl border cursor-pointer transition flex flex-col justify-between relative ${
                    selectedPlan === 'premium_799'
                      ? 'bg-indigo-950/40 border-indigo-500 shadow-md shadow-indigo-500/10'
                      : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-white flex items-center gap-1.5">
                      <Crown className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                      Premium Monthly
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-500/20 text-indigo-300">
                      Best Value
                    </span>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-black text-white">₹799</span>
                      <span className="text-xs text-slate-500 line-through">₹2,299</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">20 resumes (₹39.95/ea)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Details Box */}
            <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <UserIcon className="w-4 h-4 text-indigo-400" />
                  Account Information
                </span>
                {user && (
                  <span className="text-[11px] text-emerald-400 font-semibold">✓ Verified Account</span>
                )}
              </div>

              {user ? (
                <div className="text-xs sm:text-sm text-slate-300 space-y-1">
                  <p>Signed in as: <b className="text-white">{user.name || user.email}</b></p>
                  <p className="text-slate-400 text-xs">Email: {user.email}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-slate-400">Please sign in or create an account to activate your order and link your resumes.</p>
                  <button
                    onClick={onRequireLogin}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-bold text-white transition cursor-pointer"
                  >
                    Sign In to Proceed
                  </button>
                </div>
              )}
            </div>

            {/* Included Features */}
            <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Included with {current.name}</span>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-300">
                {current.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Column: Order Summary & Pay Action */}
          <div className="space-y-6">
            <div className="p-5 sm:p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-6 shadow-xl sticky top-6">
              <h3 className="text-base font-bold text-white border-b border-slate-800 pb-3">Order Summary</h3>

              <div className="space-y-3 text-xs sm:text-sm">
                <div className="flex justify-between text-slate-400">
                  <span>Selected Plan</span>
                  <span className="font-semibold text-slate-200 text-right">{selectedPlan === 'single_49' ? 'Pay Per Resume' : 'Premium Monthly'}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Standard Price</span>
                  <span className="line-through">₹{current.originalPrice}</span>
                </div>
                <div className="flex justify-between text-emerald-400">
                  <span>Promotional Discount ({current.discount})</span>
                  <span>-₹{current.originalPrice - current.price}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Taxes (GST)</span>
                  <span className="text-slate-300 font-medium">Included</span>
                </div>

                <div className="border-t border-slate-800 pt-3 flex justify-between items-baseline">
                  <span className="text-sm font-bold text-white">Total Amount</span>
                  <span className="text-2xl sm:text-3xl font-black text-indigo-400">₹{current.price}</span>
                </div>
              </div>

              {/* Pay Button */}
              <button
                onClick={handlePay}
                className="w-full py-4 px-4 rounded-xl font-bold text-sm bg-indigo-600 hover:bg-indigo-500 text-white transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 cursor-pointer"
              >
                <Lock className="w-4 h-4" />
                <span>Pay ₹{current.price} via Razorpay</span>
              </button>

              <div className="space-y-2 text-center">
                <p className="text-[11px] text-slate-400">
                  Accepts Google Pay, PhonePe, Paytm, UPI, Cards, NetBanking
                </p>
                <div className="flex items-center justify-center gap-2 text-[10px] text-slate-500">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Verified PCI-DSS Compliant Gateway</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
